/**
 * Hobonichi launch — Find your Techo quiz.
 *
 * This file has two halves:
 *
 *   1. Pure scoring (LINES / TIE_BREAK_ORDER / QUESTION_WEIGHTS /
 *      REASON_FRAGMENTS / scoreQuiz / buildReason). No DOM access. Loaded
 *      both by the browser AND by Node, via the offline test harness at
 *      docs/superpowers/tools/hobonichi/content/quiz-test.js (untracked —
 *      `node quiz-test.js` enumerates every answer path and asserts the
 *      scoring behaves correctly before this ships).
 *
 *   2. The quiz UI state machine (intro -> q0..qN -> result), guarded
 *      behind `typeof document !== 'undefined'` so requiring this file
 *      under Node never touches `window`/`document`.
 *
 * All question/option/result COPY lives in section blocks (Liquid) and the
 * <script type="application/json" data-hobonichi-quiz-results> tag the
 * section renders — this file only assembles it. No dependencies.
 */
(function () {
  'use strict';

  // ---------------------------------------------------------------------
  // 1. Pure scoring
  // ---------------------------------------------------------------------

  var LINES = ['original', 'planner', 'cousin', 'hon', 'weeks', 'dayfree', '5year'];

  // Tie-break order per plan Task 7 Step 3: "Ties -> cheaper/simpler line
  // wins (order: original, planner, weeks, cousin, hon, dayfree, 5year)."
  var TIE_BREAK_ORDER = ['original', 'planner', 'weeks', 'cousin', 'hon', 'dayfree', '5year'];

  // Question -> option (0-based, matching the deck's Section F option
  // order) -> { line: points }. Weights transcribed exactly from plan
  // Task 7 Step 3's baseline map.
  var QUESTION_WEIGHTS = [
    // Q1. What will it mostly be for?
    [
      { original: 3, hon: 2, '5year': 2, planner: 2 }, // 0 Journalling
      { weeks: 3, cousin: 3 }, // 1 Planning
      { cousin: 3, original: 2 }, // 2 A bit of both
      { dayfree: 3, cousin: 2 } // 3 Something creative
    ],
    // Q2. How much will you write?
    [
      { original: 3, cousin: 2, planner: 2, hon: 2 }, // 0 A page a day
      { '5year': 3, weeks: 2 }, // 1 A few lines
      { dayfree: 3, hon: 1 }, // 2 When the mood strikes
      { cousin: 3 } // 3 Lots, with a weekly view
    ],
    // Q3. Where will it live?
    [
      { weeks: 3, original: 1, planner: 1 }, // 0 In my bag
      { cousin: 2, '5year': 1 }, // 1 On my desk
      { original: 2, hon: 1 } // 2 A bit of both
    ],
    // Q4. Is this your first Hobonichi?
    [
      { original: 1, planner: 1 }, // 0 My first
      {}, // 1 Upgrading
      // Gift weight for hon RAISED from the plan's baseline +2 to +4 (5year
      // left at the baseline +2): the 432-path harness (quiz-test.js) found
      // only 52.1% of gifting paths (Q4=gift) surfaced hon or 5year in the
      // top-2, against the required >=80%. hon+4 alone (holding 5year at
      // the baseline, the smallest single-value change found to clear the
      // bar) raises that to 97.2% while keeping every other invariant green
      // (no line >45% of wins, no line at 0) — see quiz-test.js output.
      { hon: 4, '5year': 2 } // 2 A gift
    ],
    // Q5. How do you feel about Japanese?
    [
      { planner: 2, '5year': -2, dayfree: -2 }, // 0 English only
      {}, // 1 A little Japanese is fine
      {} // 2 Japanese is no barrier
    ]
  ];

  // Short "why" fragments per question/option, written to slot into the
  // deck's blurb template style ("... is your match because {{reason}}.").
  var REASON_FRAGMENTS = [
    [
      "you're using it mainly to journal",
      'you want a planner-first layout',
      'you want to plan and journal in one book',
      "you're after something creative and undated"
    ],
    [
      'you write a full page most days',
      'a few lines a day is plenty for you',
      "you'd rather write only when the mood strikes",
      'you write a lot and want a weekly view too'
    ],
    ['it needs to travel light in your bag', "it's staying on your desk", 'it moves between your bag and your desk'],
    ["it's your first Hobonichi", "you're upgrading from a previous Techo", "you're choosing it as a gift"],
    [
      "you'd like it in English",
      "a little Japanese in the book doesn't bother you",
      'Japanese in the book is no barrier for you'
    ]
  ];

  /**
   * @param {number[]} answers - 5 zero-based option indexes, one per question.
   * @returns {{winner: string, runnerUp: string, scores: Object<string, number>}}
   */
  function scoreQuiz(answers) {
    if (!Array.isArray(answers) || answers.length !== QUESTION_WEIGHTS.length) {
      throw new Error('scoreQuiz: answers must be an array of ' + QUESTION_WEIGHTS.length + ' option indexes');
    }

    var scores = {};
    LINES.forEach(function (line) {
      scores[line] = 0;
    });

    answers.forEach(function (optionIndex, questionIndex) {
      var weights = QUESTION_WEIGHTS[questionIndex] && QUESTION_WEIGHTS[questionIndex][optionIndex];
      if (!weights) return;
      Object.keys(weights).forEach(function (line) {
        scores[line] = (scores[line] || 0) + weights[line];
      });
    });

    // Hard exclusion, ADDED beyond the plan's baseline -2 point penalty:
    // the 432-path test harness (quiz-test.js) found combinations — e.g.
    // journal + few-lines + desk + gift + English-only — where the -2
    // penalty alone still let 5-Year win (raw score 6, still highest).
    // 5-Year and Day-Free are Japanese-only lines with no English edition
    // (copy-deck.md B.6/B.7): recommending either to a shopper who told us
    // "English only" is a hard business-rule violation, not a soft
    // preference, so both are disqualified from winner AND runner-up
    // whenever Q5 (language, index 4) is "English only" (option 0).
    var enOnly = answers[4] === 0;
    var eligibleOrder = TIE_BREAK_ORDER.filter(function (line) {
      return !(enOnly && (line === '5year' || line === 'dayfree'));
    });

    var ranked = eligibleOrder.slice().sort(function (a, b) {
      var diff = scores[b] - scores[a];
      if (diff !== 0) return diff;
      return eligibleOrder.indexOf(a) - eligibleOrder.indexOf(b);
    });

    return {
      winner: ranked[0],
      runnerUp: ranked[1],
      scores: scores
    };
  }

  /**
   * Assembles the {{reason}} fragment for the winning line's blurb: the
   * two questions that contributed the most points to the winner's score,
   * joined into a short clause.
   * @param {number[]} answers
   * @param {string} winnerKey
   * @returns {string}
   */
  function buildReason(answers, winnerKey) {
    var contributions = answers.map(function (optionIndex, questionIndex) {
      var weights = QUESTION_WEIGHTS[questionIndex] && QUESTION_WEIGHTS[questionIndex][optionIndex];
      var amount = (weights && weights[winnerKey]) || 0;
      var fragment = (REASON_FRAGMENTS[questionIndex] && REASON_FRAGMENTS[questionIndex][optionIndex]) || '';
      return { questionIndex: questionIndex, amount: amount, fragment: fragment };
    });

    contributions.sort(function (a, b) {
      if (b.amount !== a.amount) return b.amount - a.amount;
      return a.questionIndex - b.questionIndex;
    });

    var top = contributions
      .slice(0, 2)
      .map(function (c) {
        return c.fragment;
      })
      .filter(Boolean);

    if (top.length === 0) return 'it fits how you answered';
    if (top.length === 1) return top[0];
    return top[0] + ', and ' + top[1];
  }

  // Fallback display names, used only if a line's metaobject is still
  // DRAFT (pre-launch embargo) and so contributes no display_name of its
  // own to the results JSON — the quiz must never block on that.
  var FALLBACK_DISPLAY_NAMES = {
    original: 'Original (A6)',
    planner: 'Planner (A6)',
    cousin: 'Cousin (A5)',
    hon: 'HON (A6 / A5)',
    weeks: 'Weeks (incl. Mega)',
    dayfree: 'Day-Free (A6 / A5)',
    '5year': '5-Year Techo (incl. Gift Edition)'
  };

  // Verbatim from copy-deck.md Section F.
  var JP_DISCLOSURE_TEXT =
    'A note on language: this is a Japanese-only line. There is no English edition. ' +
    'The dated pages use plain numerals you will recognise, and the day-to-day guidance ' +
    'is in the layout rather than the words, so it stays easy to keep.';

  // ---------------------------------------------------------------------
  // 2. Browser UI (guarded so `require()` under Node never touches the DOM)
  // ---------------------------------------------------------------------

  if (typeof document !== 'undefined') {
    (function () {
      function clearElement(el) {
        while (el.firstChild) {
          el.removeChild(el.firstChild);
        }
      }

      // GA4 event passthrough. snippets/ga4-tag.liquid defines gtag on every
      // storefront page; the guard keeps this file standalone (Node harness,
      // or the snippet ever removed) — without gtag, events just no-op.
      function track(name, params) {
        if (typeof gtag === 'function') gtag('event', name, params);
      }

      function init(root) {
        var phase = root.getAttribute('data-phase') || 'teaser';
        var introScreen = root.querySelector('[data-hobonichi-quiz-screen="intro"]');
        var resultScreen = root.querySelector('[data-hobonichi-quiz-screen="result"]');
        var questionScreens = Array.prototype.slice
          .call(root.querySelectorAll('[data-hobonichi-quiz-screen^="q"]'))
          .sort(function (a, b) {
            return parseInt(a.getAttribute('data-question-index'), 10) - parseInt(b.getAttribute('data-question-index'), 10);
          });
        var progressEl = root.querySelector('[data-hobonichi-quiz-progress]');
        var progressSegments = progressEl
          ? Array.prototype.slice.call(progressEl.querySelectorAll('[data-hobonichi-quiz-progress-segment]'))
          : [];
        var progressText = root.querySelector('[data-hobonichi-quiz-progress-text]');
        var startButton = root.querySelector('[data-hobonichi-quiz-start]');

        var resultsDataEl = root.querySelector('[data-hobonichi-quiz-results]');
        var resultsByLine = {};
        if (resultsDataEl) {
          try {
            var parsed = JSON.parse(resultsDataEl.textContent || '[]');
            parsed.forEach(function (entry) {
              if (entry && entry.line_key) resultsByLine[entry.line_key] = entry;
            });
          } catch (error) {
            resultsByLine = {};
          }
        }

        var answers = questionScreens.map(function () {
          return null;
        });

        function goToQuestion(index) {
          if (index < 0 || index >= questionScreens.length) return;
          if (introScreen) introScreen.hidden = true;
          if (resultScreen) resultScreen.hidden = true;
          questionScreens.forEach(function (screen, i) {
            screen.hidden = i !== index;
          });
          if (progressEl) progressEl.hidden = false;
          progressSegments.forEach(function (segment, i) {
            segment.classList.toggle('hobonichi-quiz__progress-segment--filled', i <= index);
          });
          if (progressText) {
            progressText.textContent = 'Question ' + (index + 1) + ' of ' + questionScreens.length;
          }
          var heading = questionScreens[index].querySelector('.hobonichi-quiz__question');
          if (heading) heading.focus();
        }

        function renderResultCtas(container, result, matchEntry) {
          if (!container) return;
          clearElement(container);

          if (phase !== 'launch') return; // Teaser CTA is the email form itself; nothing to build here.

          var displayName =
            (matchEntry && matchEntry.display_name) || FALLBACK_DISPLAY_NAMES[result.winner] || result.winner;

          var primary = document.createElement('a');
          primary.className = 'button hobonichi-quiz__result-cta hobonichi-quiz__result-cta--primary';
          primary.href = '/pages/hobonichi-builder?line=' + encodeURIComponent(result.winner);
          primary.textContent = 'Build your set around the ' + displayName;
          container.appendChild(primary);

          if (matchEntry && matchEntry.page_url) {
            var secondary = document.createElement('a');
            secondary.className = 'link hobonichi-quiz__result-cta hobonichi-quiz__result-cta--secondary';
            secondary.href = matchEntry.page_url;
            secondary.textContent = 'View the book';
            container.appendChild(secondary);
          }
        }

        function showResult() {
          if (answers.indexOf(null) !== -1) return; // defensive: never score an incomplete quiz

          var result = scoreQuiz(answers);
          var reason = buildReason(answers, result.winner);
          var matchEntry = resultsByLine[result.winner] || {};
          var runnerEntry = resultsByLine[result.runnerUp] || {};

          track('quiz_complete', { match: result.winner, runner_up: result.runnerUp });

          questionScreens.forEach(function (screen) {
            screen.hidden = true;
          });
          if (introScreen) introScreen.hidden = true;
          if (progressEl) progressEl.hidden = true;

          var headingEl = root.querySelector('[data-hobonichi-quiz-result-heading]');
          var imageEl = root.querySelector('[data-hobonichi-quiz-result-image]');
          var blurbEl = root.querySelector('[data-hobonichi-quiz-result-blurb]');
          var editionEl = root.querySelector('[data-hobonichi-quiz-result-edition]');
          var runnerUpEl = root.querySelector('[data-hobonichi-quiz-result-runnerup]');
          var jpEl = root.querySelector('[data-hobonichi-quiz-result-jp]');
          var ctasEl = root.querySelector('[data-hobonichi-quiz-result-ctas]');
          var emailForm = root.querySelector('[data-hobonichi-signup]');

          var displayName = matchEntry.display_name || FALLBACK_DISPLAY_NAMES[result.winner] || result.winner;
          var runnerDisplayName =
            runnerEntry.display_name || FALLBACK_DISPLAY_NAMES[result.runnerUp] || result.runnerUp;

          if (headingEl) headingEl.textContent = 'Your match: ' + displayName;

          if (imageEl) {
            clearElement(imageEl);
            if (matchEntry.card_image) {
              var img = document.createElement('img');
              img.src = matchEntry.card_image;
              img.alt = displayName;
              img.loading = 'lazy';
              imageEl.appendChild(img);
            }
          }

          if (blurbEl) {
            var blurbTemplate = matchEntry.blurb || '';
            // Blocks store the placeholder as [reason], not {{reason}} —
            // Shopify's theme-asset-upload validator rejects text/textarea
            // setting VALUES in template JSON that contain literal {{ }} or
            // {% %} ("can't include Liquid syntax... without valid dynamic
            // sources"), and that failure is atomic (the whole theme fails
            // to upload). [reason] is brace-free and reads naturally to a
            // merchant editing the block.
            blurbEl.textContent = blurbTemplate.replace('[reason]', reason);
          }

          if (editionEl) {
            var editionFragment = matchEntry.edition_line || '';
            editionEl.textContent = editionFragment ? editionFragment + ' · Jan-start ships 1 September.' : '';
          }

          if (runnerUpEl) {
            var diffFragment = runnerEntry.runner_up_line || '';
            // The runner_up_line fragments carry their own internal commas
            // ("the roomier daily book, and the only line with..."), so
            // joining one to the line name with another comma reads as a
            // three-item list. Sentence-case it and let it stand alone.
            var diffSentence = diffFragment.charAt(0).toUpperCase() + diffFragment.slice(1);
            runnerUpEl.textContent = diffFragment
              ? 'Also worth a look: ' + runnerDisplayName + '. ' + diffSentence + '.'
              : '';
          }

          if (jpEl) {
            if (matchEntry.jp_disclosure) {
              jpEl.textContent = JP_DISCLOSURE_TEXT;
              jpEl.hidden = false;
            } else {
              jpEl.textContent = '';
              jpEl.hidden = true;
            }
          }

          renderResultCtas(ctasEl, result, matchEntry);

          if (emailForm) {
            emailForm.setAttribute('data-properties', JSON.stringify({
              hobonichi_signup_source: 'quiz',
              hobonichi_match: result.winner
            }));
          }

          if (resultScreen) {
            resultScreen.hidden = false;
          }
          if (headingEl) headingEl.focus();
        }

        if (startButton) {
          startButton.addEventListener('click', function () {
            track('quiz_start', {});
            goToQuestion(0);
          });
        }

        questionScreens.forEach(function (screen) {
          var qIndex = parseInt(screen.getAttribute('data-question-index'), 10);

          var optionButtons = screen.querySelectorAll('[data-hobonichi-quiz-option]');
          optionButtons.forEach(function (button) {
            button.addEventListener('click', function () {
              var optionIndex = parseInt(button.getAttribute('data-option-index'), 10);
              // First answer per question only — re-answers after Back would
              // otherwise inflate the per-question funnel counts.
              if (answers[qIndex] === null) {
                track('quiz_step', { question: qIndex + 1, option: optionIndex + 1 });
              }
              answers[qIndex] = optionIndex;

              optionButtons.forEach(function (b) {
                b.classList.toggle('hobonichi-quiz__option--selected', b === button);
              });

              if (qIndex + 1 < questionScreens.length) {
                goToQuestion(qIndex + 1);
              } else {
                showResult();
              }
            });
          });

          var backButton = screen.querySelector('[data-hobonichi-quiz-back]');
          if (backButton) {
            backButton.addEventListener('click', function () {
              goToQuestion(qIndex - 1);
            });
          }
        });
      }

      try {
        var roots = document.querySelectorAll('[data-hobonichi-quiz]');
        roots.forEach(init);
      } catch (error) {
        /* no-op — defensive: the quiz failing to bind should never break the rest of the page */
      }
    })();
  }

  // ---------------------------------------------------------------------
  // Exports
  // ---------------------------------------------------------------------

  if (typeof window !== 'undefined') {
    window.HobonichiQuiz = { scoreQuiz: scoreQuiz, buildReason: buildReason };
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { scoreQuiz: scoreQuiz, buildReason: buildReason };
  }
})();
